import { useTheme } from '@emotion/react';
import { CarRepair, Construction } from '@mui/icons-material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Button, Card, CircularProgress, Dialog, DialogContent, Grid, Menu, MenuItem, Stack, Typography, useMediaQuery } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { useNavigate } from 'react-router-dom';
import ReviewRequestModal from '../../../common/ReviewRequestModal';
import { appointmentKeys, useAppointmentDetails, useKanbanBoardList, useUpdateAppointmentStatus } from '../../../services/business/appointment';
import { useGetShopConfig } from '../../../services/core/settings/settings.hooks';
import { USER_TIMEZONE } from '../../../utility/Constant';
import SuccessMessageModal from '../../Common/SuccessMessage';
import AddOdoModal from '../../modal/AddOdoModal';
import AppointmentModel from '../AppointmentModel';

dayjs.extend(utc);
dayjs.extend(timezone);

/** Matches backend today filter: dropOff in [startOfDay, endOfDay) for the kanban board timezone. */
const isDropOffDateToday = (dropOffDateTime, tz) => {
    if (!dropOffDateTime) return true;
    try {
        return dayjs(dropOffDateTime).tz(tz).isSame(dayjs().tz(tz), 'day');
    } catch {
        return true;
    }
};

const updateKanbanColumnCounts = (updatedDataData, statusKey, delta) => {
    const possibleKeys = [`${statusKey}Count`, `${statusKey}_count`, `${statusKey}_Count`];
    possibleKeys.forEach((key) => {
        if (updatedDataData[key] !== undefined) {
            updatedDataData[key] = Math.max(0, (updatedDataData[key] || 0) + delta);
        }
    });
};

/**
 * Optimistic kanban cache update with the following rules:
 *
 * - TODAY's appointment   → always move to the destination column (never remove)
 * - PAST appointment      → moving to vehicle_dropped_off / in_progress / ready_for_pick_up → keep (move normally)
 * - PAST appointment      → moving to waiting_for_drop_off → REMOVE from board
 *                           (it is in the past, so it no longer belongs on today's board)
 *
 * @param {string} appointmentId - Mongo id only (no query string)
 */
const patchKanbanCacheForStatusChange = (oldData, { appointmentId, newStatus, timeZone: boardTz, sourceStatus }) => {
    if (!oldData?.data || !appointmentId) return oldData;

    const updatedData = { ...oldData };
    const updatedDataData = { ...updatedData.data };

    // ── 1. Locate the appointment in the cache ──────────────────────────────
    let currentStatus = sourceStatus ?? null;
    let appointmentToMove = null;

    if (currentStatus && Array.isArray(updatedDataData[currentStatus])) {
        appointmentToMove = updatedDataData[currentStatus].find((item) => item._id === appointmentId);
    }

    if (!appointmentToMove) {
        currentStatus = null;
        Object.keys(updatedDataData).forEach((columnKey) => {
            if (Array.isArray(updatedDataData[columnKey])) {
                const found = updatedDataData[columnKey].find((item) => item._id === appointmentId);
                if (found) {
                    currentStatus = columnKey;
                    appointmentToMove = found;
                }
            }
        });
    }

    if (!appointmentToMove || !currentStatus) return oldData;

    // ── 2. Decide whether to remove or move ────────────────────────────────
    const isToday = isDropOffDateToday(appointmentToMove.dropOffDateTime, boardTz);

    // Past appointment being sent back to "waiting_for_drop_off" → remove from board.
    // (It belongs to a previous day and has no place in today's waiting column.)
    const removeFromBoard = !isToday && newStatus === 'waiting_for_drop_off';

    // ── 3. Remove from source column ───────────────────────────────────────
    updatedDataData[currentStatus] = updatedDataData[currentStatus].filter((item) => item._id !== appointmentId);
    updateKanbanColumnCounts(updatedDataData, currentStatus, -1);

    if (removeFromBoard) {
        return {
            ...updatedData,
            data: updatedDataData,
        };
    }

    // ── 4. Insert into destination column & re-sort ────────────────────────
    const updatedAppointment = { ...appointmentToMove, status: newStatus };
    const destinationColumn = [...(updatedDataData[newStatus] || []), updatedAppointment];
    updatedDataData[newStatus] = destinationColumn.sort((a, b) => {
        const dateA = new Date(a.dropOffDateTime);
        const dateB = new Date(b.dropOffDateTime);
        return dateA - dateB;
    });
    updateKanbanColumnCounts(updatedDataData, newStatus, 1);

    return {
        ...updatedData,
        data: updatedDataData,
    };
};

const styleTag = {
    borderRadius: '48px',
    color: 'black',
    padding: '10px 20px',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    margin: '10px',
    width: '200px',
};
const styleButton = {
    borderRadius: '48px',
    color: 'black',
    padding: '6px 16px',
    justifyContent: 'center',
    alignItems: 'center',
};
const styleDiv = {
    width: '400px',
    height: '56px',
    alignItems: 'center',
    display: 'flex',
    padding: '24px 16px',
    alignSelf: 'stretch',
    borderRadius: '8px',
    position: 'relative',
};
const styleTypography = {
    padding: '8px',
    borderStyle: 'solid',
    borderColor: 'white',
    borderWidth: '1px',
    borderRadius: '40px',
    marginLeft: '20px',
    backgroundColor: 'white',
    color: 'black',
    display: 'flex',
    width: '40px',
    height: '40px',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute',
    right: '8px',
    fontSize: '20px',
    fontWeight: '500',
    lineHeight: '24px',
};
const TodayAppointment = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [id, setId] = useState('');
    const [status, setStatus] = useState('');
    const [openSuccessModal, setOpenSuccessModal] = React.useState(false);
    const [successMessage, setSuccessMessage] = React.useState('');
    const [show, setShow] = React.useState(false);
    const [data, setData] = React.useState('');
    const [selectId, setSelectId] = React.useState('');
    const [updateStatus, setUpdateStatus] = React.useState('');
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [dragId, setDragId] = React.useState('');
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
    const [open, setOpen] = React.useState(false);
    const [appointmentDropOffODO, setAppointmentDropOffODo] = useState(null);
    const [appointmentCompletionOffODO, setAppointmentCompletionOffODo] = useState(null);
    let timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [selectedId, setSelectedId] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('');
    const [userReviewModalData, setUserReviewModalData] = useState('');
    const [reviewModalLastSentOn, setReviewModalLastSentOn] = useState('');
    const [isRefreshingDetails, setIsRefreshingDetails] = useState(false);

    // Use TanStack Query for shop config instead of Redux
    const { data: shopConfigData, isLoading: isConfigLoading, error: configError } = useGetShopConfig();
    console.log('🚀 ~ TodayAppointment ~ shopConfigData:', shopConfigData);

    // Use TanStack Query for kanban board list instead of Redux
    const { data: kanbanData, isLoading: isKanbanLoading, refetch: refetchKanban } = useKanbanBoardList(timeZone);

    // Use TanStack Query mutation for updating appointment status
    const updateStatusMutation = useUpdateAppointmentStatus();

    // Use TanStack Query for appointment details with enabled flag
    const { data: appointmentDetailData, isLoading: isDetailLoading, error: detailError } = useAppointmentDetails(id);

    const SHOP_NAME = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('apex-saas_details'))?.shopName || '';
        } catch (e) {
            return '';
        }
    }, []);

    // Function to refresh appointment details (for modal) and show loader during refetch
    const refreshAppointmentDetails = async () => {
        if (!id) return;
        setIsRefreshingDetails(true);
        try {
            await queryClient.invalidateQueries({
                queryKey: appointmentKeys.details(id),
            });
        } finally {
            setIsRefreshingDetails(false);
        }
    };
    // Set data when appointment details are fetched
    useEffect(() => {
        if (appointmentDetailData?.data?._id) {
            setData(appointmentDetailData?.data);
        }
    }, [appointmentDetailData]);

    // Extract appointmentReviewManualSelect from shop config data
    const appointmentReviewManualSelect = shopConfigData?.config?.appointmentReviewManualSelect;

    const open1 = Boolean(anchorEl);
    const handleClick1 = (event, statusData) => {
        setAnchorEl(event.currentTarget);
        setStatus(statusData);
    };
    const handleClose1 = () => {
        setAnchorEl(null);
    };

    // Use TanStack Query data for kanban board instead of Redux selector
    const Data = kanbanData?.data || {};

    const handleStatusClose = () => {
        refetchKanban();
        setShow(false);
    };
    const handleStatus = () => setShow(true);

    const onDragEnd = async (result, columns, setColumns) => {
        if (!result.destination) return;
        const { source, destination } = result;
        setAppointmentDropOffODo(null);
        setAppointmentCompletionOffODo(null);

        let found = columns?.[source?.droppableId]?.items?.filter((d) => d?._id == result?.draggableId);

        if (Array.isArray(found) && found?.[0]?.dropOffODO) {
            setAppointmentDropOffODo(found?.[0]?.dropOffODO);
        }
        if (Array.isArray(found) && found?.[0]?.completionODO) {
            setAppointmentCompletionOffODo(found?.[0]?.completionODO);
        }
        let key = source.droppableId;
        let dragIndex = source.index;

        if (source.droppableId !== destination.droppableId) {
            const sourceColumn = columns[source.droppableId];
            const destColumn = columns[destination.droppableId];
            const sourceItems = [...sourceColumn.items];
            const destItems = [...destColumn.items];
            const [removed] = sourceItems.splice(source.index, 1);
            destItems.splice(destination.index, 0, removed);
            setColumns({
                ...columns,
                [source.droppableId]: {
                    ...sourceColumn,
                    items: sourceItems,
                },
                [destination.droppableId]: {
                    ...destColumn,
                    items: destItems,
                },
            });
            let payload = {
                status: destination.droppableId,
            };
            if (
                source.droppableId == 'waiting_for_drop_off' &&
                (destination.droppableId == 'vehicle_dropped_off' ||
                    destination.droppableId == 'in_progress' ||
                    destination.droppableId == 'ready_for_pick_up' ||
                    destination.droppableId == 'customer_paid' ||
                    destination.droppableId == 'mark_as_no_show')
            ) {
                // if (source.droppableId == "waiting_for_drop_off" || source.droppableId == "in_progress")
                if (
                    (source.droppableId == 'waiting_for_drop_off' &&
                        (destination.droppableId == 'vehicle_dropped_off' ||
                            destination.droppableId == 'in_progress' ||
                            destination.droppableId == 'ready_for_pick_up' ||
                            destination.droppableId == 'customer_paid')) ||
                    (source.droppableId == 'in_progress' && destination.droppableId == 'ready_for_pick_up')
                ) {
                    setStatus(source.droppableId);
                    setUpdateStatus(destination.droppableId);
                    // handleStatus();
                    setDragId(Data[key][dragIndex]._id);
                    const statusUpdate = await updateStatusMutation.mutateAsync({
                        id: Data[key][dragIndex]._id,
                        payload: { status: destination.droppableId },
                    });
                    if (statusUpdate) {
                        // TanStack Query will automatically invalidate and refetch
                    }
                    // dragableId = Data[key][dragIndex]._id;
                } else {
                    if (Data[key][dragIndex]._id) {
                        const statusUpdate = await updateStatusMutation.mutateAsync({
                            id: Data[key][dragIndex]._id,
                            payload,
                        });
                        if (statusUpdate) {
                            // TanStack Query will automatically invalidate and refetch
                        }
                    }
                }
            } else if (
                source.droppableId == 'vehicle_dropped_off' &&
                (destination.droppableId == 'waiting_for_drop_off' ||
                    destination.droppableId == 'in_progress' ||
                    destination.droppableId == 'ready_for_pick_up' ||
                    destination.droppableId == 'customer_paid')
            ) {
                if (
                    source.droppableId == 'vehicle_dropped_off' &&
                    (destination.droppableId == 'ready_for_pick_up' || destination.droppableId == 'customer_paid')
                    //          ||
                    // (source.droppableId == "in_progress" && destination.droppableId == "ready_for_pick_up")
                ) {
                    setStatus(source.droppableId);
                    setUpdateStatus(destination.droppableId);
                    // handleStatus();

                    const statusUpdate = await updateStatusMutation.mutateAsync({
                        id: Data[key][dragIndex]._id,
                        payload: { status: destination.droppableId },
                    });
                    if (statusUpdate) {
                        // TanStack Query will automatically invalidate and refetch
                    }

                    setDragId(Data[key][dragIndex]._id);
                    // dragableId = Data[key][dragIndex]._id;
                } else {
                    // console.log("Sdfsdfsdfsdfsdfsd", Data[key][dragIndex]?._id);
                    if (Data[key][dragIndex]?._id) {
                        const statusUpdate = await updateStatusMutation.mutateAsync({
                            id: Data[key][dragIndex]._id,
                            payload,
                        });
                        if (statusUpdate) {
                            // TanStack Query will automatically invalidate and refetch
                        }
                    }
                }
            } else if (
                source.droppableId == 'in_progress' &&
                (destination.droppableId == 'vehicle_dropped_off' ||
                    destination.droppableId == 'ready_for_pick_up' ||
                    destination.droppableId == 'customer_paid')
            ) {
                if (
                    source.droppableId == 'in_progress' &&
                    (destination.droppableId == 'ready_for_pick_up' || destination.droppableId == 'customer_paid')
                    //          ||
                    // (source.droppableId == "in_progress" && destination.droppableId == "ready_for_pick_up")
                ) {
                    setStatus(source.droppableId);
                    setUpdateStatus(destination.droppableId);
                    // handleStatus();

                    const statusUpdate = await updateStatusMutation.mutateAsync({
                        id: Data[key][dragIndex]._id,
                        payload: { status: destination.droppableId },
                    });
                    if (statusUpdate) {
                        // TanStack Query will automatically invalidate and refetch
                    }
                    setDragId(Data[key][dragIndex]._id);
                    // dragableId = Data[key][dragIndex]._id;
                } else {
                    if (Data[key][dragIndex]?._id) {
                        const statusUpdate = await updateStatusMutation.mutateAsync({
                            id: Data[key][dragIndex]._id,
                            payload,
                        });
                        if (statusUpdate) {
                            // TanStack Query will automatically invalidate and refetch
                        }
                    }
                }
            } else if (source.droppableId == 'ready_for_pick_up' && (destination.droppableId == 'in_progress' || destination.droppableId == 'customer_paid')) {
                if (
                    (source.droppableId == 'waiting_for_drop_off' &&
                        (destination.droppableId == 'vehicle_dropped_off' ||
                            destination.droppableId == 'ready_for_pick_up' ||
                            destination.droppableId == 'customer_paid')) ||
                    (source.droppableId == 'in_progress' && destination.droppableId == 'ready_for_pick_up')
                ) {
                    setStatus(source.droppableId);
                    setUpdateStatus(destination.droppableId);
                    // handleStatus();
                    const statusUpdate = await updateStatusMutation.mutateAsync({
                        id: Data[key][dragIndex]._id,
                        payload: { status: destination.droppableId },
                    });
                    if (statusUpdate) {
                        // TanStack Query will automatically invalidate and refetch
                    }
                    if (statusUpdate) {
                        refetchKanban();
                    }
                    setDragId(Data[key][dragIndex]._id);
                    // dragableId = Data[key][dragIndex]._id;
                } else {
                    if (Data[key][dragIndex]._id) {
                        const statusUpdate = await updateStatusMutation.mutateAsync({
                            id: Data[key][dragIndex]._id,
                            payload,
                        });
                        if (statusUpdate) {
                            // TanStack Query will automatically invalidate and refetch
                        }
                    }
                }
            } else {
                handleStatusClose();
            }
        } else {
            const column = columns[source.droppableId];
            const copiedItems = [...column.items];
            const [removed] = copiedItems.splice(source.index, 1);
            copiedItems.splice(destination.index, 0, removed);
            setColumns({
                ...columns,
                [source.droppableId]: {
                    ...column,
                    items: copiedItems,
                },
            });
        }
    };

    const onDragEndTwo = async (result, columns, setColumns) => {
        if (!result.destination) return;

        const { source, destination } = result;
        const appointmentId = result.draggableId;
        const newStatus = destination.droppableId;
        const sourceStatus = source.droppableId;

        if (source.droppableId !== destination.droppableId && !appointmentId) {
            console.error('onDragEndTwo: missing appointment id for cross-column drag');
            return;
        }

        setAppointmentDropOffODo(null);
        setAppointmentCompletionOffODo(null);

        let found = columns?.[source?.droppableId]?.items?.filter((d) => d?._id == result?.draggableId);

        if (Array.isArray(found) && found?.[0]?.dropOffODO) {
            setAppointmentDropOffODo(found?.[0]?.dropOffODO);
        }
        if (Array.isArray(found) && found?.[0]?.completionODO) {
            setAppointmentCompletionOffODo(found?.[0]?.completionODO);
        }

        if (source.droppableId !== destination.droppableId) {
            // 1. OPTIMISTIC UPDATE - Update cache immediately
            queryClient.setQueryData(appointmentKeys.kanbanBoard(timeZone), (oldData) =>
                patchKanbanCacheForStatusChange(oldData, {
                    appointmentId,
                    newStatus,
                    timeZone,
                    sourceStatus,
                })
            );

            const draggedAppt = columns[source.droppableId]?.items?.find((item) => item._id === appointmentId);
            const isToday = draggedAppt && isDropOffDateToday(draggedAppt.dropOffDateTime, timeZone);

            // Remove from board only when: past appointment → moved to waiting_for_drop_off
            const removeFromBoard = draggedAppt && !isToday && newStatus === 'waiting_for_drop_off';

            // 2. Update local columns state for immediate UI feedback
            if (removeFromBoard) {
                const filterOut = (items) => (Array.isArray(items) ? items.filter((item) => item._id !== appointmentId) : items);
                setColumns({
                    waiting_for_drop_off: { ...columns.waiting_for_drop_off, items: filterOut(columns.waiting_for_drop_off.items) },
                    vehicle_dropped_off: { ...columns.vehicle_dropped_off, items: filterOut(columns.vehicle_dropped_off.items) },
                    in_progress: { ...columns.in_progress, items: filterOut(columns.in_progress.items) },
                    ready_for_pick_up: { ...columns.ready_for_pick_up, items: filterOut(columns.ready_for_pick_up.items) },
                });
            } else {
                const sourceColumn = columns[source.droppableId];
                const destColumn = columns[destination.droppableId];
                const sourceItems = [...sourceColumn.items];
                const destItems = [...destColumn.items];
                const [removed] = sourceItems.splice(source.index, 1);
                destItems.splice(destination.index, 0, removed);
                setColumns({
                    ...columns,
                    [source.droppableId]: {
                        ...sourceColumn,
                        items: sourceItems,
                    },
                    [destination.droppableId]: {
                        ...destColumn,
                        items: destItems,
                    },
                });
            }

            // 3. API CALL - Update server (background)
            try {
                setStatus(source.droppableId);
                setUpdateStatus(destination.droppableId);
                setDragId(appointmentId);

                await updateStatusMutation.mutateAsync({
                    id: appointmentId,
                    payload: { status: newStatus },
                });

                // ✅ Do NOT invalidate on success — the optimistic cache is already
                // correct. Invalidating immediately triggers a refetch that causes
                // the card to flicker back to its old position before settling.
                // The cache will be refreshed on the next natural focus/mount event.
            } catch (error) {
                // 4. REVERT on failure — invalidate to pull real server state back
                await queryClient.invalidateQueries({
                    queryKey: appointmentKeys.kanbanBoard(timeZone),
                });
                console.error('Failed to update appointment status:', error);
            }
        } else {
            // If moving within the same column, just reorder
            const column = columns[source.droppableId];
            const copiedItems = [...column.items];
            const [removed] = copiedItems.splice(source.index, 1);
            copiedItems.splice(destination.index, 0, removed);
            setColumns({
                ...columns,
                [source.droppableId]: {
                    ...column,
                    items: copiedItems,
                },
            });
        }
    };
    const handleOpen = (detailsData) => {
        setStatus(detailsData?.status);
        setId(detailsData?._id);
        setOpen(true);
    };

    const handleClose = () => {
        setId('');
        setOpen(false);
        refetchKanban();
    };

    const [columns, setColumns] = useState({
        waiting_for_drop_off: {
            name: 'Waiting for Drop Off',
            items: Data?.waiting_for_drop_off,
        },
        vehicle_dropped_off: {
            name: 'Vehicles Dropped Off',
            items: Data?.vehicle_dropped_off,
        },
        in_progress: {
            name: 'In Progress',
            items: Data?.in_progress,
        },
        ready_for_pick_up: {
            name: 'Ready for Pick Up',
            items: Data?.ready_for_pick_up,
        },
    });

    useEffect(() => {
        // ✅ While a status mutation is in-flight, the optimistic cache update has
        // already positioned the card correctly via setColumns. If we sync from
        // Data here, we overwrite that and cause the "snap back then snap forward"
        // flicker. Only sync when the board is fully settled (no pending mutation).
        if (updateStatusMutation.isPending) return;

        // Sort appointments by dropOffDateTime in ascending order (earliest first)
        const sortByDropOffDateTime = (appointments) => {
            if (!Array.isArray(appointments)) return appointments;
            return [...appointments].sort((a, b) => {
                const dateA = new Date(a.dropOffDateTime);
                const dateB = new Date(b.dropOffDateTime);
                return dateA - dateB; // Ascending order (12pm, 1pm, 5pm)
            });
        };

        setColumns((prev) => ({
            waiting_for_drop_off: { ...prev.waiting_for_drop_off, items: sortByDropOffDateTime(Data?.waiting_for_drop_off) },
            vehicle_dropped_off: { ...prev.vehicle_dropped_off, items: sortByDropOffDateTime(Data?.vehicle_dropped_off) },
            in_progress: { ...prev.in_progress, items: sortByDropOffDateTime(Data?.in_progress) },
            ready_for_pick_up: { ...prev.ready_for_pick_up, items: sortByDropOffDateTime(Data?.ready_for_pick_up) },
        }));
    }, [Data, updateStatusMutation.isPending]);

    // TanStack Query automatically handles kanban board data fetching, no need for manual kanbanBoardData

    const UpdateStatus = async (id, status) => {
        const payload = {
            status: status,
        };
        try {
            const statusUpdate = await updateStatusMutation.mutateAsync({ id, payload });
            if (statusUpdate) {
                handleClose();
                navigate('/appointment/today');
            }
        } catch (error) {
            console.error('Failed to update appointment status:', error);
        }
    };
    const handleUpdateAppointmentStatus = async (id, status) => {
        if (!id) {
            console.error('handleUpdateAppointmentStatus: missing appointment id');
            handleClose1();
            return;
        }

        handleClose1();

        const bareAppointmentId = typeof id === 'string' ? id.split('?')[0] : id;

        // 1. OPTIMISTIC UPDATE
        queryClient.setQueryData(appointmentKeys.kanbanBoard(timeZone), (oldData) =>
            patchKanbanCacheForStatusChange(oldData, {
                appointmentId: bareAppointmentId,
                newStatus: status,
                timeZone,
            })
        );

        // 2. API CALL
        const payload = {
            status: status,
        };
        try {
            await updateStatusMutation.mutateAsync({ id: bareAppointmentId, payload });

            // ✅ Optimistic cache is already correct — skip invalidate to avoid flicker.
            setOpenSuccessModal(true);
            setSuccessMessage('Appointment status updated successfully');
            setAnchorEl(null);
            setSelectId(null);
        } catch (error) {
            // Revert on failure — pull real server state
            await queryClient.invalidateQueries({
                queryKey: appointmentKeys.kanbanBoard(timeZone),
            });
            console.error('Failed to update appointment status:', error);
        }
    };
    const handleUpdateAppointmentStatusModal = async (id, status, queryString, customMessage) => {
        const bareAppointmentId = typeof id === 'string' ? id.split('?')[0] : id;

        // 1. OPTIMISTIC UPDATE
        queryClient.setQueryData(appointmentKeys.kanbanBoard(timeZone), (oldData) =>
            patchKanbanCacheForStatusChange(oldData, {
                appointmentId: bareAppointmentId,
                newStatus: status,
                timeZone,
            })
        );

        // 2. API CALL (customMessage in body only when truthy)
        try {
            const payload = { status, ...(customMessage && { customMessage }) };
            const fullUrl = `${bareAppointmentId}${queryString ? `?${queryString}` : ''}`;

            await updateStatusMutation.mutateAsync({ id: fullUrl, payload });

            // ✅ Optimistic cache is already correct — skip invalidate to avoid flicker.
            setOpenSuccessModal(true);
            setSuccessMessage('Appointment status updated successfully');
            setAnchorEl(null);
            setSelectId(null);
        } catch (error) {
            // Revert on failure — pull real server state
            await queryClient.invalidateQueries({
                queryKey: appointmentKeys.kanbanBoard(timeZone),
            });
            console.error('Failed to update appointment status:', error);
        }
    };

    const handleCustomerPaidClick = () => {
        // Reset ODOs
        setAppointmentDropOffODo(null);
        setAppointmentCompletionOffODo(null);

        const found = columns?.[status]?.items?.filter((d) => d._id === selectId);

        if (Array.isArray(found) && found[0]?.dropOffODO) {
            setAppointmentDropOffODo(found[0].dropOffODO);
        }
        if (Array.isArray(found) && found[0]?.completionODO) {
            setAppointmentCompletionOffODo(found[0].completionODO);
        }

        if (!appointmentReviewManualSelect) {
            setUpdateStatus('customer_paid');
            handleUpdateAppointmentStatus(selectId, 'customer_paid');
            return;
        }

        const googleReviewRequest = found?.[0]?.googleReviewRequest;
        if (googleReviewRequest?.reviewCompleted === true) {
            setUpdateStatus('customer_paid');
            handleUpdateAppointmentStatus(selectId, 'customer_paid');
            return;
        }

        setSelectedId(selectId);
        setSelectedStatus('customer_paid');
        handleClose1();
        setUserReviewModalData(found);

        const list = Array.isArray(found?.[0]?.previousGoogleReviewRequests) ? found[0].previousGoogleReviewRequests : [];
        const latestGroup = list.length > 0 ? list[0] : null;
        const lastSentAt = latestGroup?.lastSentAt;
        const reviewRequests = Array.isArray(latestGroup?.reviewRequest) ? latestGroup.reviewRequest : [];
        const lastReq = reviewRequests.length > 0 ? reviewRequests[reviewRequests.length - 1] : null;

        const isWithin24Hours = lastSentAt && dayjs(lastSentAt).tz(USER_TIMEZONE).isAfter(dayjs().tz(USER_TIMEZONE).subtract(24, 'hours'));

        let lastSentOnMessage = '';

        if (!latestGroup || list.length === 0 || !lastSentAt || reviewRequests.length === 0 || !lastReq) {
            lastSentOnMessage = '';
        } else {
            const channels = [];

            if (lastReq?.sms?.sent) channels.push('text');
            if (lastReq?.email?.sent) channels.push('email');
            if (lastReq?.notification?.sent) channels.push('app');

            let channelsLabel = '—';
            if (channels.length === 1) {
                channelsLabel = channels[0];
            } else if (channels.length === 2) {
                channelsLabel = `${channels[0]} and ${channels[1]}`;
            } else if (channels.length >= 3) {
                channelsLabel = `${channels[0]}, ${channels[1]} and ${channels[2]}`;
            }

            const formattedDate = dayjs(lastSentAt).tz(USER_TIMEZONE).format('DD-MMMM-YYYY');

            if (isWithin24Hours) {
                lastSentOnMessage = `Last sent via ${channelsLabel} today`;
            } else if (list.length === 1) {
                lastSentOnMessage = `Last sent via ${channelsLabel} on ${formattedDate}`;
            } else {
                lastSentOnMessage = `${list.length} review requests sent previously. Last sent via ${channelsLabel} on ${formattedDate}`;
            }
        }

        setReviewModalLastSentOn(lastSentOnMessage);
        setShowReviewModal(true);
    };
    const platform = shopConfigData?.config?.appointmentReviewPlatform;

    const modalTitle = platform === 'google' ? 'Send a Google review request?' : 'Send a review request?';

    const showInstructionText =
        platform === 'google'
            ? 'Grow your online reputation by requesting a Google review. Skip if they have already left a review or if you want to follow-up privately.'
            : platform === 'apex_auto'
              ? 'The customer will be prompted to leave the review on the Apex Auto filtration system. 4+ star reviews will be prompted to also leave a review on Google.'
              : 'Default instruction for other platforms.';

    const googleImage = platform === 'google';

    return (
        <div className="today_appointment_container">
            <div className="appointmentCardGridContainer">
                <DragDropContext onDragEnd={(result) => onDragEndTwo(result, columns, setColumns)}>
                    {Object.entries(columns)?.map(([columnId, column], index) => {
                        return (
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    rowGap: '16px',
                                    width: '430px',
                                    // flex:'0 0 auto'
                                    // height: "100%",
                                }}
                                key={columnId}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'flex-start',
                                        alignItems: 'center',
                                        // paddingInline: "16px",
                                    }}
                                >
                                    <Typography
                                        variant="title"
                                        fontWeight={500}
                                        fontSize={'16px'}
                                        style={{
                                            ...styleDiv,
                                            backgroundColor:
                                                column.name == 'Waiting for Drop Off'
                                                    ? '#75DBFF'
                                                    : column.name == 'Vehicles Dropped Off'
                                                      ? '#FFC30F'
                                                      : column.name == 'In Progress'
                                                        ? '#FF9E31'
                                                        : '#5CE469',
                                        }}
                                    >
                                        {column.name}
                                        <Typography style={styleTypography}> {column?.items?.length ? column?.items?.length : 0}</Typography>
                                    </Typography>
                                </div>
                                <div style={{ height: '100%' }}>
                                    <Droppable droppableId={columnId} key={columnId}>
                                        {(provided, snapshot) => {
                                            return (
                                                <div
                                                    {...provided.droppableProps}
                                                    ref={provided.innerRef}
                                                    style={{
                                                        background: snapshot.isDraggingOver ? 'lightgrey' : '#0000001F',
                                                        padding: 0,
                                                        width: 400,
                                                        minHeight: 500,
                                                        borderRadius: '8px',
                                                        height: '100%',
                                                    }}
                                                >
                                                    {column?.items?.map((item, index) => {
                                                        return (
                                                            <Draggable key={item._id} draggableId={item._id} index={index}>
                                                                {(provided, snapshot) => {
                                                                    return (
                                                                        <div
                                                                            ref={provided.innerRef}
                                                                            {...provided.draggableProps}
                                                                            {...provided.dragHandleProps}
                                                                            style={{
                                                                                userSelect: 'none',
                                                                                minHeight: '100px',
                                                                                color: 'white',
                                                                                ...provided.draggableProps.style,
                                                                            }}
                                                                        >
                                                                            <Grid
                                                                                container
                                                                                item
                                                                                xs={12}
                                                                                sm={12}
                                                                                md={12}
                                                                                xl={12}
                                                                                padding={'8px 16px'}
                                                                                paddingTop={'16px'}
                                                                            >
                                                                                <Card
                                                                                    border={'1px solid #ffffff'}
                                                                                    style={{ borderRadius: '16px' }}
                                                                                    key={item._id}
                                                                                >
                                                                                    <Grid
                                                                                        container
                                                                                        padding={'16px 24px'}
                                                                                        rowGap={'16px'}
                                                                                        borderRadius={'16px'}
                                                                                        onClick={() => {
                                                                                            handleOpen(item);
                                                                                        }}
                                                                                    >
                                                                                        <Grid item xs={12} sm={12} md={12} xl={12}>
                                                                                            <Stack direction={'row'} spacing={2}>
                                                                                                <Stack>
                                                                                                    <Typography
                                                                                                        variant="body1"
                                                                                                        fontWeight={500}
                                                                                                        fontSize={'20px'}
                                                                                                    >
                                                                                                        {item?.appointmentNumber}
                                                                                                    </Typography>
                                                                                                </Stack>
                                                                                                <Stack>
                                                                                                    <Typography
                                                                                                        variant="body1"
                                                                                                        fontWeight={500}
                                                                                                        fontSize={'20px'}
                                                                                                    >
                                                                                                        {item?.customer?.firstName
                                                                                                            ? item?.customer?.firstName + ' '
                                                                                                            : ''}
                                                                                                        {item?.customer?.lastName
                                                                                                            ? item?.customer?.lastName
                                                                                                            : ''}
                                                                                                    </Typography>
                                                                                                </Stack>
                                                                                            </Stack>
                                                                                        </Grid>
                                                                                        <Grid item xs={12} sm={12} md={12} xl={12}>
                                                                                            <Stack direction={'row'} spacing={2}>
                                                                                                <Stack>
                                                                                                    <Typography
                                                                                                        variant="body1"
                                                                                                        fontWeight={500}
                                                                                                        fontSize={'16px'}
                                                                                                    >
                                                                                                        <CarRepair style={{ color: 'gray' }} />
                                                                                                    </Typography>
                                                                                                </Stack>
                                                                                                <Stack>
                                                                                                    <Typography
                                                                                                        variant="body1"
                                                                                                        fontWeight={400}
                                                                                                        fontSize={'16px'}
                                                                                                        color={'#000000'}
                                                                                                    >
                                                                                                        {item?.vehicle?.year ? item?.vehicle?.year : ''}
                                                                                                        {' ' +
                                                                                                            item?.vehicle?.vehicleMake?.name +
                                                                                                            ' ' +
                                                                                                            item?.vehicle?.vehicleModel?.name}
                                                                                                        {item?.vehicle?.vehicleModelTrim?.name
                                                                                                            ? ' ' + item?.vehicle?.vehicleModelTrim?.name
                                                                                                            : ''}
                                                                                                    </Typography>
                                                                                                </Stack>
                                                                                            </Stack>
                                                                                        </Grid>
                                                                                        <Grid item xs={12} sm={12} md={12} xl={12}>
                                                                                            <Stack direction={'row'} spacing={2}>
                                                                                                <Stack>
                                                                                                    <Typography
                                                                                                        variant="body1"
                                                                                                        fontWeight={500}
                                                                                                        fontSize={'16px'}
                                                                                                    >
                                                                                                        <CalendarTodayIcon style={{ color: 'gray' }} />
                                                                                                    </Typography>
                                                                                                </Stack>
                                                                                                <Stack>
                                                                                                    <Typography
                                                                                                        variant="body1"
                                                                                                        fontWeight={400}
                                                                                                        fontSize={'16px'}
                                                                                                        color={'#000000'}
                                                                                                    >
                                                                                                        {moment(item?.dropOffDateTime).format(
                                                                                                            'MMM D, YYYY hh:mm A'
                                                                                                        )}
                                                                                                    </Typography>
                                                                                                </Stack>
                                                                                            </Stack>
                                                                                        </Grid>
                                                                                        {item?.services?.length > 0 && (
                                                                                            <Grid item xs={12} sm={12} md={12} xl={12}>
                                                                                                <Stack direction={'row'} spacing={2}>
                                                                                                    <Stack>
                                                                                                        <Typography
                                                                                                            variant="body1"
                                                                                                            fontWeight={500}
                                                                                                            fontSize={'16px'}
                                                                                                        >
                                                                                                            <Construction
                                                                                                                style={{
                                                                                                                    color: 'gray',
                                                                                                                }}
                                                                                                            />
                                                                                                        </Typography>
                                                                                                    </Stack>

                                                                                                    <Stack>
                                                                                                        <Typography
                                                                                                            variant="body1"
                                                                                                            fontWeight={400}
                                                                                                            fontSize={'16px'}
                                                                                                            color={'#000000'}
                                                                                                        >
                                                                                                            {item?.serviceCount > 1
                                                                                                                ? item?.serviceCount + ' ' + 'Total Services'
                                                                                                                : item?.services[0].name}
                                                                                                        </Typography>
                                                                                                    </Stack>
                                                                                                </Stack>
                                                                                            </Grid>
                                                                                        )}
                                                                                    </Grid>
                                                                                    <Grid
                                                                                        item
                                                                                        container
                                                                                        xs={12}
                                                                                        sm={12}
                                                                                        md={12}
                                                                                        xl={12}
                                                                                        justifyContent={'end'}
                                                                                        padding={'6px 20px'}
                                                                                        marginBottom={'20px'}
                                                                                    >
                                                                                        <Grid item>
                                                                                            <Button
                                                                                                variant="contained"
                                                                                                endIcon={<ExpandMoreIcon />}
                                                                                                style={{
                                                                                                    ...styleButton,
                                                                                                    backgroundColor:
                                                                                                        column.name == 'Waiting for Drop Off'
                                                                                                            ? '#75DBFF'
                                                                                                            : column.name == 'Vehicles Dropped Off'
                                                                                                              ? '#FFC30F'
                                                                                                              : column.name == 'In Progress'
                                                                                                                ? '#FF9E31'
                                                                                                                : '#5CE469',
                                                                                                }}
                                                                                                aria-controls={open1 ? 'demo-positioned-menu' : undefined}
                                                                                                aria-haspopup="true"
                                                                                                aria-expanded={open1 ? 'true' : undefined}
                                                                                                onClick={(e) => {
                                                                                                    setSelectId(item?._id);
                                                                                                    handleClick1(e, item?.status);
                                                                                                }}
                                                                                            >
                                                                                                {column.name == 'Waiting for Drop Off'
                                                                                                    ? 'WAITING FOR DROP OFF'
                                                                                                    : column.name == 'Vehicles Dropped Off'
                                                                                                      ? 'VEHICLE DROPPED OFF'
                                                                                                      : column.name == 'In Progress'
                                                                                                        ? 'IN PROGRESS'
                                                                                                        : 'READY FOR PICK UP'}
                                                                                            </Button>

                                                                                            <Menu
                                                                                                style={{
                                                                                                    width: '400px',
                                                                                                    border: 'none',
                                                                                                    boxShadow: 'none !important',
                                                                                                }}
                                                                                                id="demo-positioned-menu"
                                                                                                anchorEl={anchorEl}
                                                                                                open={open1}
                                                                                                onClose={handleClose1}
                                                                                                anchorOrigin={{
                                                                                                    vertical: 'top',
                                                                                                    horizontal: 'left',
                                                                                                }}
                                                                                                transformOrigin={{
                                                                                                    vertical: 'top',
                                                                                                    horizontal: 'left',
                                                                                                }}
                                                                                            >
                                                                                                <MenuItem>Update Status</MenuItem>
                                                                                                <MenuItem
                                                                                                    onClick={async () => {
                                                                                                        setUpdateStatus('waiting_for_drop_off');
                                                                                                        handleUpdateAppointmentStatus(
                                                                                                            selectId,
                                                                                                            'waiting_for_drop_off'
                                                                                                        );
                                                                                                    }}
                                                                                                    // disabled={true}
                                                                                                    style={{
                                                                                                        ...styleTag,
                                                                                                        backgroundColor: '#75DBFF',
                                                                                                    }}
                                                                                                >
                                                                                                    Waiting for Drop Off
                                                                                                </MenuItem>

                                                                                                <MenuItem
                                                                                                    onClick={() => {
                                                                                                        setUpdateStatus('vehicle_dropped_off');
                                                                                                        handleUpdateAppointmentStatus(
                                                                                                            selectId,
                                                                                                            'vehicle_dropped_off'
                                                                                                        );
                                                                                                    }}
                                                                                                    style={{
                                                                                                        ...styleTag,
                                                                                                        background: '#FFC30F',
                                                                                                    }}
                                                                                                >
                                                                                                    Vehicle Dropped Off
                                                                                                </MenuItem>

                                                                                                <MenuItem
                                                                                                    onClick={() => {
                                                                                                        setUpdateStatus('in_progress');
                                                                                                        handleUpdateAppointmentStatus(selectId, 'in_progress');
                                                                                                    }}
                                                                                                    style={{
                                                                                                        ...styleTag,
                                                                                                        background: '#FF9E31',
                                                                                                    }}
                                                                                                >
                                                                                                    In Progress
                                                                                                </MenuItem>

                                                                                                <MenuItem
                                                                                                    onClick={() => {
                                                                                                        setUpdateStatus('ready_for_pick_up');

                                                                                                        setAppointmentDropOffODo(null);
                                                                                                        setAppointmentCompletionOffODo(null);

                                                                                                        let found = columns?.[status]?.items?.filter(
                                                                                                            (d) => d._id == selectId
                                                                                                        );

                                                                                                        if (Array.isArray(found) && found?.[0]?.dropOffODO) {
                                                                                                            setAppointmentDropOffODo(found?.[0]?.dropOffODO);
                                                                                                        }
                                                                                                        if (Array.isArray(found) && found?.[0]?.completionODO) {
                                                                                                            setAppointmentCompletionOffODo(
                                                                                                                found?.[0]?.completionODO
                                                                                                            );
                                                                                                        }

                                                                                                        handleUpdateAppointmentStatus(
                                                                                                            selectId,
                                                                                                            'ready_for_pick_up'
                                                                                                        );
                                                                                                    }}
                                                                                                    style={{
                                                                                                        ...styleTag,
                                                                                                        background: '#5CE469',
                                                                                                    }}
                                                                                                >
                                                                                                    Ready for Pick Up
                                                                                                </MenuItem>
                                                                                                <MenuItem
                                                                                                    onClick={handleCustomerPaidClick}
                                                                                                    style={{
                                                                                                        ...styleTag,
                                                                                                        background: '#00CD44',
                                                                                                    }}
                                                                                                >
                                                                                                    Customer Paid
                                                                                                </MenuItem>

                                                                                                <MenuItem
                                                                                                    onClick={() => {
                                                                                                        setUpdateStatus('mark_as_no_show');
                                                                                                        handleUpdateAppointmentStatus(
                                                                                                            selectId,
                                                                                                            'mark_as_no_show'
                                                                                                        );
                                                                                                    }}
                                                                                                    style={{
                                                                                                        ...styleTag,
                                                                                                        background: '#F44336',
                                                                                                    }}
                                                                                                >
                                                                                                    Mark as No Show
                                                                                                </MenuItem>
                                                                                            </Menu>
                                                                                        </Grid>
                                                                                    </Grid>
                                                                                </Card>
                                                                            </Grid>
                                                                        </div>
                                                                    );
                                                                }}
                                                            </Draggable>
                                                        );
                                                    })}
                                                    {provided.placeholder}
                                                </div>
                                            );
                                        }}
                                    </Droppable>
                                </div>
                            </div>
                        );
                    })}
                </DragDropContext>
            </div>
            {(status == 'waiting_for_drop_off' || status == 'in_progress' || status == 'vehicle_dropped_off') && (
                <AddOdoModal
                    open={show}
                    handleClose={handleStatusClose}
                    updateStatus={updateStatus}
                    id={dragId ? dragId : selectId}
                    handleClose1={handleClose1}
                    currentStatus={status}
                    handleClose2={handleClose}
                    appointmentDropOffODO={appointmentDropOffODO}
                    appointmentCompletionOffODO={appointmentCompletionOffODO}
                    setAppointmentCompletionOffODo={setAppointmentCompletionOffODo}
                    setAppointmentDropOffODo={setAppointmentDropOffODo}
                    appointmentView={() => {}}
                />
            )}

            <AppointmentModel
                open={open}
                handleClose={handleClose}
                data={data}
                status={status}
                show={show}
                handleStatusClose={handleStatusClose}
                handleStatus={handleStatus}
                recallDetailsAPI={refreshAppointmentDetails}
                fetchAppointmentDetails={refreshAppointmentDetails}
                setAppointmentCompletionOffODo={setAppointmentCompletionOffODo}
                setAppointmentDropOffODo={setAppointmentDropOffODo}
                columns={columns}
                setUpdateStatus={setUpdateStatus}
                setSelectId={setSelectId}
            />
            <SuccessMessageModal message={successMessage} open={openSuccessModal} setOpenSuccessModal={setOpenSuccessModal} />

            <Dialog fullScreen={false} open={isKanbanLoading || updateStatusMutation.isPending || isDetailLoading || isRefreshingDetails}>
                <DialogContent>
                    <CircularProgress />
                </DialogContent>
            </Dialog>
            <ReviewRequestModal
                shopConfigData={shopConfigData}
                open={showReviewModal}
                onClose={() => {
                    setShowReviewModal(false);
                    setReviewModalLastSentOn('');
                }}
                modalTitle={modalTitle}
                customerName={`${userReviewModalData?.[0]?.customer?.firstName || ''} ${userReviewModalData?.[0]?.customer?.lastName || ''}`}
                carInfo={`${userReviewModalData?.[0]?.vehicle?.vehicleYear || userReviewModalData?.[0]?.vehicle?.year || ''} ${userReviewModalData?.[0]?.vehicle?.vehicleMake?.name || ''} ${
                    userReviewModalData?.[0]?.vehicle?.vehicleModel?.name || ''
                } ${userReviewModalData?.[0]?.vehicle?.vehicleModelTrim?.name || ''}`.trim()}
                googleImage={googleImage}
                showInstructionText={showInstructionText}
                googleReviewMessage={reviewModalLastSentOn}
                shopName={SHOP_NAME}
                sendRequest={(queryString, customMessage) => {
                    handleUpdateAppointmentStatusModal(selectedId, selectedStatus, queryString, customMessage);
                    setShowReviewModal(false);
                    setReviewModalLastSentOn('');
                }}
                onClickSkip={() => {
                    setShowReviewModal(false);
                    setReviewModalLastSentOn('');
                }}
            />
        </div>
    );
};

export default TodayAppointment;
